import { NextAuthConfig, Session, User, type DefaultSession } from "next-auth"
import { DefaultJWT, JWT } from "next-auth/jwt"
import CredentialsProvider from "next-auth/providers/credentials"
import { BASEURL_API } from "."

export type AddressDetails = {
  _id: string
  userId: string
  orgId?: string | null
  deleted_at?: string | null
  city?: string
  address?: string
  isPrimary: boolean
  createdAt?: string
  updatedAt?: string
}

export type LoggedInUser = {
  _id: string
  name: string
  gender: string
  password: string
  email: string
  phone: string
  role: number
  designation?: string | null
  image: string
  addressDetails: AddressDetails[]
}

export type LoginResponse = {
  message: string
  token: string
  user: LoggedInUser[]
}

/**
 * Module augmentation for `next-auth` types. Allows us to add custom properties to the `session`
 * object and keep type safety.
 *
 * @see https://next-auth.js.org/getting-started/typescript#module-augmentation
 */

declare module "next-auth" {
  interface Session extends DefaultSession {
    user: {
      // id?: string
      token?: string
      user: LoginResponse["user"][0]
      // ...other properties
    } & DefaultSession["user"]
  }

  export interface AdapterUser {
    // id?: string
    token?: string
    user: LoginResponse["user"]
  }

  interface User {
    // id?: string
    token?: string
    user: LoginResponse["user"]
  }
}

declare module "next-auth/jwt" {
  /**
   * Returned by `useSession`, `getSession` and received as a prop on the `SessionProvider` React Context
   */

  interface JWT extends DefaultJWT {
    // id?: string
    token?: string
    user: LoginResponse["user"][0]
  }
}

/**
 * Options for NextAuth.js used to configure adapters, providers, callbacks, etc.
 *
 * @see https://next-auth.js.org/configuration/options
 */
export const authConfig = {
  providers: [
    CredentialsProvider({
      id: "credentials",
      name: "Credentials",
      credentials: {
        email: { label: "Email", type: "email" },
        password: { label: "Password", type: "password" },
      },
      async authorize(credentials) {
        const res = await fetch(
          `${BASEURL_API}/edifyweb-backend/v1/auth/login/otp`,
          {
            method: "POST",
            body: JSON.stringify({
              phone: credentials.email,
              otp: credentials.password,
            }),
            headers: {
              "Content-Type": "application/json",
            },
          }
        )

        const body = await res.json()

        if (res.status === 200) {
          return body
        }
        return null
      },
    }),
  ],
  session: {
    strategy: "jwt",
  },
  trustHost: true,
  secret: process.env.NEXTAUTH_SECRET,
  callbacks: {
    jwt: async ({ token, user }: { token: JWT; user: User }) => {
      if (user) {
        token.token = user.token
        token.user = user.user[0]
      }

      return token
    },
    session: async ({ session, token }: { session: Session; token: JWT }) => {
      if (token) {
        session.user.token = token.token
        // @ts-expect-error @this is type error (but this is valid)
        session.user.user = token.user
      }
      return session
    },
  },
} satisfies NextAuthConfig
